// =============================================================
// VARIANCE — The Executive Command Center
// File      : Assets.tsx
// Location  : /home/Applications/variance/web/src/pages/Assets.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Premium Assets / Properties Register with KPI
//             header, dense table, and slide-out details panel.
// =============================================================

import { useState, useEffect } from 'react';
import { Plus, X, LandPlot, Building2, Truck, Box, FileText } from 'lucide-react';

interface Company { id: string; name: string; }
interface Asset {
  id: string;
  asset_type: string;
  type_label: string | null;
  code: string;
  name: string;
  company_id: string;
  company_name: string;
  area_value: number | null;
  area_unit: string | null;
  location: string | null;
  purchase_value: number | null;
  current_value: number | null;
  amount_paid: number | null;
  balance_to_pay: number | null;
  purchased_from: string | null;
  loan_mortgage_details: string | null;
  notes: string | null;
}

export function Assets() {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Form State
  const [formData, setFormData] = useState({
    company_id: '',
    asset_type: 'land',
    custom_type: '',
    code: '',
    name: '',
    area_value: '',
    area_unit: 'Acres',
    location: '',
    purchased_from: '',
    purchase_value: '',
    current_value: '',
    amount_paid: '',
    loan_mortgage_details: '',
    notes: ''
  });
  const [formError, setFormError] = useState('');

  const fetchAssets = () => {
    setIsLoading(true);
    fetch('/api/assets')
      .then(res => res.json())
      .then(data => { if (!data.error) setAssets(data); setIsLoading(false); })
      .catch(() => setIsLoading(false));
  };

  const fetchCompanies = () => {
    fetch('/api/companies')
      .then(res => res.json())
      .then(data => {
        if (!data.error && data.length > 0) {
          setCompanies(data);
          setFormData(prev => ({ ...prev, company_id: data[0].id }));
        }
      });
  };

  useEffect(() => {
    fetchAssets();
    fetchCompanies();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      const res = await fetch('/api/assets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          type_label: formData.asset_type === 'other' ? formData.custom_type : undefined,
          area_value: formData.area_value ? parseFloat(formData.area_value) : undefined,
          purchase_value: formData.purchase_value ? parseFloat(formData.purchase_value) : undefined,
          current_value: formData.current_value ? parseFloat(formData.current_value) : undefined,
          amount_paid: formData.amount_paid ? parseFloat(formData.amount_paid) : undefined,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create asset');
      
      setIsModalOpen(false);
      setFormData({ company_id: companies[0]?.id || '', asset_type: 'land', custom_type: '', code: '', name: '', area_value: '', area_unit: 'Acres', location: '', purchased_from: '', purchase_value: '', current_value: '', amount_paid: '', loan_mortgage_details: '', notes: '' });
      fetchAssets();
    } catch (err: any) {
      setFormError(err.message);
    }
  };

  // Helper to get icons based on asset type
  const getAssetIcon = (type: string) => {
    switch(type) {
      case 'land': return <LandPlot size={18} />;
      case 'building': return <Building2 size={18} />;
      case 'machinery': return <Truck size={18} />;
      default: return <Box size={18} />;
    }
  };

  // Calculate KPIs
  const totalValue = assets.reduce((sum, a) => sum + (a.current_value || 0), 0);
  const totalBalance = assets.reduce((sum, a) => sum + (a.balance_to_pay || 0), 0);

  return (
    <div className="space-y-6 relative min-h-screen">
      
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Assets / Properties</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Manage Fixed Assets, Land Bank, and Financial Liabilities.</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white hover:opacity-90 transition-all shadow-[0_0_15px_var(--brand-accent)]" style={{ backgroundColor: 'var(--brand-accent)' }}>
          <Plus size={18} /> Add Asset
        </button>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
          <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Total Assets</div>
          <div className="text-3xl font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{assets.length}</div>
        </div>
        <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
          <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Total Current Value</div>
          <div className="text-3xl font-bold font-mono" style={{ color: 'var(--text-primary)' }}>₹{totalValue.toLocaleString('en-IN')}</div>
        </div>
        <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
          <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Total Balance to Pay</div>
          <div className="text-3xl font-bold font-mono" style={{ color: totalBalance > 0 ? '#EF4444' : '#22C55E' }}>₹{totalBalance.toLocaleString('en-IN')}</div>
        </div>
      </div>

      {/* Dense Data Table */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
              <th className="p-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Asset Name / Code</th>
              <th className="p-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Company</th>
              <th className="p-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Location</th>
              <th className="p-4 text-xs font-semibold uppercase tracking-wider text-right" style={{ color: 'var(--text-secondary)' }}>Purchase Value</th>
              <th className="p-4 text-xs font-semibold uppercase tracking-wider text-right" style={{ color: 'var(--text-secondary)' }}>Current Value</th>
              <th className="p-4 text-xs font-semibold uppercase tracking-wider text-right" style={{ color: 'var(--text-secondary)' }}>Balance</th>
              <th className="p-4 text-xs font-semibold uppercase tracking-wider text-center" style={{ color: 'var(--text-secondary)' }}>View</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="p-8 text-center text-gray-400">Loading...</td></tr>
            ) : assets.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-gray-400">No assets registered yet.</td></tr>
            ) : (
              assets.map(a => (
                <tr key={a.id} className="border-t hover:bg-[var(--bg-panel-alt)] transition-colors cursor-pointer" style={{ borderColor: 'var(--border-subtle)' }} onClick={() => setSelectedAsset(a)}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[var(--bg-panel-alt)]" style={{ color: 'var(--brand-accent)' }}>
                        {getAssetIcon(a.asset_type)}
                      </div>
                      <div>
                        <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{a.name}</div>
                        <div className="text-xs font-mono text-orange-500">[{a.code}]</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{a.company_name}</td>
                  <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{a.location || '-'}</td>
                  <td className="p-4 text-sm font-mono text-right" style={{ color: 'var(--text-secondary)' }}>₹{(a.purchase_value || 0).toLocaleString('en-IN')}</td>
                  <td className="p-4 text-sm font-mono text-right" style={{ color: 'var(--text-primary)' }}>₹{(a.current_value || 0).toLocaleString('en-IN')}</td>
                  <td className="p-4 text-sm font-mono text-right" style={{ color: a.balance_to_pay && a.balance_to_pay > 0 ? '#EF4444' : '#22C55E' }}>
                    {a.balance_to_pay && a.balance_to_pay > 0 ? `₹${a.balance_to_pay.toLocaleString('en-IN')}` : 'Paid'}
                  </td>
                  <td className="p-4 text-center">
                    <button className="p-1 rounded text-gray-400 hover:text-white hover:bg-[var(--brand-accent)] transition-colors">
                      <FileText size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* SLIDE-OUT DETAILS PANEL */}
      {selectedAsset && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm transition-opacity">
          <div className="w-full max-w-md h-full flex flex-col shadow-2xl animate-slide-in-right" style={{ backgroundColor: 'var(--bg-panel)', borderLeft: '1px solid var(--border-subtle)' }}>
            {/* Panel Header */}
            <div className="p-6 border-b flex justify-between items-start" style={{ borderColor: 'var(--border-subtle)' }}>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{selectedAsset.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-mono px-2 py-0.5 rounded bg-[var(--bg-panel-alt)] text-orange-500">{selectedAsset.code}</span>
                  <span className="text-xs capitalize" style={{ color: 'var(--text-secondary)' }}>{selectedAsset.asset_type === 'other' && selectedAsset.type_label ? selectedAsset.type_label : selectedAsset.asset_type}</span>
                </div>
              </div>
              <button onClick={() => setSelectedAsset(null)} className="p-2 rounded-full hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ color: 'var(--text-secondary)' }}>
                <X size={20} />
              </button>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              
              {/* Financials */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--brand-accent)' }}>Financials</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Purchased From</span>
                    <span className="font-medium text-right" style={{ color: 'var(--text-primary)' }}>{selectedAsset.purchased_from || '-'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Purchase Value</span>
                    <span className="font-mono" style={{ color: 'var(--text-primary)' }}>₹{(selectedAsset.purchase_value || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Current Value</span>
                    <span className="font-mono text-green-500">₹{(selectedAsset.current_value || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Amount Paid</span>
                    <span className="font-mono" style={{ color: 'var(--text-primary)' }}>₹{(selectedAsset.amount_paid || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Balance</span>
                    <span className="font-mono" style={{ color: selectedAsset.balance_to_pay && selectedAsset.balance_to_pay > 0 ? '#EF4444' : '#22C55E' }}>
                      ₹{(selectedAsset.balance_to_pay || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="pt-2">
                    <span className="block mb-1" style={{ color: 'var(--text-secondary)' }}>Loan / Mortgage</span>
                    <div className="p-3 rounded-lg bg-[var(--bg-panel-alt)] text-xs" style={{ color: 'var(--text-primary)' }}>
                      {selectedAsset.loan_mortgage_details || 'No active loans.'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--brand-accent)' }}>Property Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Location</span>
                    <span style={{ color: 'var(--text-primary)' }}>{selectedAsset.location || '-'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Area / Size</span>
                    <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{selectedAsset.area_value ? `${selectedAsset.area_value} ${selectedAsset.area_unit}` : '-'}</span>
                  </div>
                  <div className="pt-2">
                    <span className="block mb-1" style={{ color: 'var(--text-secondary)' }}>Notes</span>
                    <div className="p-3 rounded-lg bg-[var(--bg-panel-alt)] text-xs" style={{ color: 'var(--text-primary)' }}>
                      {selectedAsset.notes || 'No notes available.'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD ASSET MODAL (Same as before) */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto py-10">
          <div className="w-full max-w-2xl p-6 rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <h2 className="text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Add New Asset / Property</h2>
            {formError && <div className="p-3 mb-4 rounded text-sm text-red-500 bg-red-500/10">{formError}</div>}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Company</label>
                  <select required value={formData.company_id} onChange={e => setFormData({...formData, company_id: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]">
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Asset Type</label>
                  <select value={formData.asset_type} onChange={e => setFormData({...formData, asset_type: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]">
                    <option value="land">Land Bank</option>
                    <option value="building">Building / Flat</option>
                    <option value="machinery">Machinery</option>
                    <option value="other">+ Custom Type</option>
                  </select>
                  {formData.asset_type === 'other' && (
                    <input required type="text" placeholder="Custom type" value={formData.custom_type} onChange={e => setFormData({...formData, custom_type: e.target.value})} className="w-full mt-2 p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Code</label>
                  <input required type="text" value={formData.code} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Name</label>
                  <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Location</label>
                  <input type="text" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Area (Size)</label>
                  <div className="flex gap-2">
                    <input type="number" value={formData.area_value} onChange={e => setFormData({...formData, area_value: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                    <input type="text" value={formData.area_unit} onChange={e => setFormData({...formData, area_unit: e.target.value})} className="w-20 p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-[var(--bg-panel-alt)] space-y-4 border border-[var(--border-subtle)] mt-4">
                <h3 className="text-sm font-semibold" style={{ color: 'var(--brand-accent)' }}>Financial & Legal Details</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Purchased From</label>
                    <input type="text" placeholder="Seller / Authority" value={formData.purchased_from} onChange={e => setFormData({...formData, purchased_from: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Purchase Value (₹)</label>
                    <input type="number" value={formData.purchase_value} onChange={e => setFormData({...formData, purchase_value: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Current Value (₹)</label>
                    <input type="number" value={formData.current_value} onChange={e => setFormData({...formData, current_value: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Amount Paid (₹)</label>
                    <input type="number" value={formData.amount_paid} onChange={e => setFormData({...formData, amount_paid: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Loan / Mortgage Details</label>
                  <input type="text" placeholder="Bank Name, Loan A/C" value={formData.loan_mortgage_details} onChange={e => setFormData({...formData, loan_mortgage_details: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
              </div>

              <div className="flex gap-3 pt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-2 rounded font-medium bg-[var(--bg-panel-alt)] text-[var(--text-primary)]">Cancel</button>
                <button type="submit" className="flex-1 py-2 rounded font-medium text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Asset</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
